import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import get from 'lodash.get';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import { isArrayWithItems } from '../../../../helpers/arrayHelper';
import { Record as RecordHelper } from '../../../../modules/bin/utility';
import CurrencyHelper from '../../../../helpers/currencyHelper';
import { Loader } from 'semantic-ui-react';

import { summaryOptions, chartColors } from '../../constants';

// Custom tooltip component
const CustomTooltip = (props) => {
  const { active, payload, label, summaryType, currencySymbol } = props;

  if (!active || !payload || !payload.length) {
    return null;
  }

  const summaryOption = summaryOptions.find((item) => item.key === summaryType);
  const prefix = summaryOption?.isCurrency
    ? currencySymbol
    : summaryOption?.prefix || '';

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        padding: '8px 12px',
        color: '#333',
        fontSize: 12,
        border: '1px solid #e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}
    >
      <div style={{ marginBottom: 4, fontWeight: 600 }}>
        {new Date(label).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
      </div>
      {payload.map((entry, index) => (
        <div key={index} style={{ color: entry.color }}>
          {entry.name}: {prefix}{typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
        </div>
      ))}
    </div>
  );
};

const CompareChart = ({ summaryType, models }) => {
  // Check if all records are ready
  const isAllRecordsReady = useMemo(() => {
    if (isArrayWithItems(models)) {
      return models.every((model) => RecordHelper.isRecordReady(model.summaryDataRecord));
    }
    return true;
  }, [models]);

  const isLoading = !isAllRecordsReady;

  const currencyCode =
    get(models, '[0].summaryDataRecord.data.data.currency') ||
    get(models, '[0].summaryRecord.data.data.currency') ||
    get(models, '[0].currency');
  const currencySymbol = CurrencyHelper.getCurrencySymbol(currencyCode);

  const noData = useMemo(() => {
    return (
      !isArrayWithItems(models) ||
      models.every((model) => !isArrayWithItems(get(model.summaryDataRecord, 'data.data.list')))
    );
  }, [models]);

  // Transform data for Recharts - combine all model data into unified dataset
  const chartData = useMemo(() => {
    if (!isArrayWithItems(models)) return [];

    // Get all dates from all models
    const allDatesMap = new Map();

    models.forEach((model, modelIndex) => {
      const summaryData = get(model.summaryDataRecord, 'data.data.list') || [];
      summaryData.forEach((item) => {
        const dateKey = new Date(item.date).getTime();
        if (!allDatesMap.has(dateKey)) {
          allDatesMap.set(dateKey, { timestamp: dateKey });
        }
        allDatesMap.get(dateKey)[`value${modelIndex}`] = item[summaryType] || 0;
      });
    });

    // Sort by date and return array
    return Array.from(allDatesMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [models, summaryType]);

  // Format axis tick
  const formatXAxisTick = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const formatYAxisTick = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  };

  if (isLoading) {
    return (
      <div className="warning-container">
        <Loader active inline />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="warning-container no-items">
        {I18n.t('dashboard.top-area.chart.no-items')}
      </div>
    );
  }

  return (
    <div id="dashboard-chart" style={{ width: '100%', height: 300 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeOpacity={0.07} vertical={false} />

          <XAxis
            dataKey="timestamp"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatXAxisTick}
            tick={{ fontSize: 11, fill: '#666' }}
            axisLine={{ stroke: '#e0e0e0' }}
            tickLine={false}
            minTickGap={50}
          />

          <YAxis
            tickFormatter={formatYAxisTick}
            tick={{ fontSize: 11, fill: '#666' }}
            axisLine={false}
            tickLine={false}
            width={50}
          />

          <Tooltip content={<CustomTooltip summaryType={summaryType} currencySymbol={currencySymbol} />} />

          <Legend
            verticalAlign="top"
            height={36}
            wrapperStyle={{ paddingBottom: 10 }}
          />

          {/* Render a line for each model */}
          {models.map((model, index) => (
            <Line
              key={model.id || index}
              type="monotone"
              dataKey={`value${index}`}
              name={model.name}
              stroke={chartColors[index] || '#8884d8'}
              fill={chartColors[index] || '#8884d8'}
              strokeWidth={2}
              dot={{ r: 3, fill: chartColors[index] || '#8884d8', stroke: '#fff', strokeWidth: 1 }}
              activeDot={{ r: 5, fill: chartColors[index] || '#8884d8', stroke: '#fff', strokeWidth: 2 }}
              animationDuration={1000}
              animationEasing="ease-out"
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const mapState = ({ dashboard }) => {
  return {
    summaryType: dashboard.summaryType,
    models: dashboard.compare.models
  };
};

const CompareChartContainer = connect(mapState, null)(CompareChart);
export default CompareChartContainer;
